// Gedeeld toast-systeem. Vervangt de losse ad-hoc "floating" meldingen (o.a. de
// Strava-koppel-banner in App.jsx) door één primitive met consistente positie,
// timing, kleur en aria-live. Inline knop-feedback (bv. "✓ Gekopieerd" op een
// deelknop) blijft bewust contextueel op de knop staan — dat is geen toast.
//
// Gebruik:
//   const toast = useToast();
//   toast('Strava gekoppeld!');                     // default (success)
//   toast('Koppelen mislukt.', { type: 'error' });  // rood, blijft langer
import { useCallback, useRef, useState } from 'react';
import { ToastContext } from './toast-context';

// Kleur per type; sluit aan op de semantische theme-tokens.
const TYPE_BG = {
  success: 'var(--green)',
  error: 'var(--red)',
  info: 'var(--primary)',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback(id => {
    setToasts(list => list.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, { type = 'success', duration } = {}) => {
    const id = ++seq.current;
    const ms = duration ?? (type === 'error' ? 5000 : 4000);
    setToasts(list => [...list, { id, message, type }]);
    if (ms > 0) setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none"
        role="status"
        aria-live="polite"
      >
        {toasts.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            className="pointer-events-auto text-[#fff] py-2.5 px-[22px] rounded-lg text-[15px] font-semibold shadow-[0_4px_16px_rgba(0,0,0,0.18)] font-display tracking-[0.04em] cursor-pointer border-none animate-[sweep-in_var(--transition)]"
            style={{ background: TYPE_BG[t.type] ?? TYPE_BG.success }}
            aria-label={`${t.message} (tik om te sluiten)`}
          >
            {t.message}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
